import React from "react";

interface DeeplinkButtonProps {
  userId: number;
  tab?: string;
  sessionId?: number;
  uploadId?: string;
  className?: string;
  children?: React.ReactNode;
  variant?: "icon" | "text" | "badge";
  title?: string;
}

/**
 * A button that navigates to the Users page with deep linking parameters.
 * Opens in the same tab by navigating to /surfr-ui/users?userId=...
 */
export const DeeplinkButton: React.FC<DeeplinkButtonProps> = ({
  userId,
  tab = "user-details",
  sessionId,
  uploadId,
  className = "",
  children,
  variant = "icon",
  title = "View in Users page",
}) => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    const params = new URLSearchParams();
    params.set("userId", String(userId));
    if (tab) params.set("tab", tab);
    if (sessionId) params.set("sessionId", String(sessionId));
    if (uploadId) params.set("uploadId", uploadId);

    // Navigate to the Users page with the deeplink parameters
    const url = `/surfr-ui/users?${params.toString()}`;
    window.open(url, "_blank");
  };

  // Icon-only variant (for compact displays)
  if (variant === "icon") {
    return (
      <button
        onClick={handleClick}
        className={`inline-flex items-center justify-center p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${className}`}
        title={title}
      >
        <svg
          className="w-4 h-4 text-cyan-500 hover:text-cyan-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
          />
        </svg>
      </button>
    );
  }

  // Badge variant (shows ID with external link icon)
  if (variant === "badge") {
    return (
      <button
        onClick={handleClick}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 hover:bg-cyan-100 dark:hover:bg-cyan-900/50 text-gray-700 dark:text-gray-300 hover:text-cyan-700 dark:hover:text-cyan-300 text-xs font-mono transition-colors ${className}`}
        title={title}
      >
        #{sessionId || userId}
        <svg
          className="w-3 h-3"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
          />
        </svg>
      </button>
    );
  }

  // Text variant (for inline text with children)
  return (
    <button
      onClick={handleClick}
      className={`inline-flex items-center gap-1 text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 hover:underline transition-colors ${className}`}
      title={title}
    >
      {children || `#${userId}`}
      <svg
        className="w-3.5 h-3.5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
        />
      </svg>
    </button>
  );
};
